import {
  createStyles,
  Paper,
  Popper,
  Tooltip as MUToolTip,
  withStyles,
} from '@material-ui/core'
import { TooltipProps } from '@material-ui/core/Tooltip'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import arrowGenerator from '../../utils/arrowGenerator'
import { useDispatch, useSelector } from 'react-redux'
import { getTooltips } from '../../reducers/modals/selectors/getModals'
import { closeTooltip, openTooltip } from '../../actions/Modals/openModal'
import { ModalActions } from '../../reducers/modals/actionNames'

const styles = createStyles({
  arrow: {
    '&::before': {
      borderStyle: 'solid',
      content: '""',
      display: 'block',
      height: 0,
      margin: 'auto',
      width: 0,
    },
    fontSize: 6,
    height: '3em',
    position: 'absolute',
    width: '3em',
  },
  arrowPopper: arrowGenerator('#fff'),
  placementBottom: {
    margin: '8px 0',
  },
  placementLeft: {
    margin: '0 8px',
  },
  placementRight: {
    margin: '0 8px',
  },
  placementTop: {
    margin: '8px 0',
  },
  popper: arrowGenerator('#fff'),
  tooltip: {
    backgroundColor: '#fff',
    boxShadow: '0px 0px 4px rgba(0,0,0,0.25)',
    color: 'black',
    fontSize: '11px',
    position: 'relative',
  },
  tooltipBreak: {
    backgroundColor: '#fff',
    boxShadow: '0px 0px 4px rgba(0,0,0,0.25)',
    color: 'black',
    fontSize: '11px',
    position: 'relative',
    wordBreak: 'break-all',
  },
})

function Tooltip({
  children,
  title,
  classes,
  disableHoverListener = false,
  disableClickListener = undefined,
  disableTouchListener = undefined,
  withArrow = true,
  placement = 'bottom',
  isRoom = false,
  size = 1,
  whiteSpace = true,
  wordBreakAll = true,
  disablePropagation = false,
  overCursor = false,
                   withoutWrapper = false,
  onClick,
  tooltipId = Math.round(Math.random() * 1000),
  viewportBoundary = false,
  openEvent = 'onClick',
}: ITooltipProps) {
  const [arrowRef, setArrowRef] = React.useState()
  const handleRef = node => setArrowRef(node)
  const [mousePos, setMousePos] = useState<any>()
  const tooltipRef = useRef(null)
  const tooltips = useSelector(getTooltips)
  const dispatch = useDispatch()

  const open = tooltips.includes(String(tooltipId))

  const setOpen = open => {
    if (open) {
      dispatch({ type: ModalActions.CloseTooltips })
      return dispatch(openTooltip(String(tooltipId)))
    }
    if (!open) return dispatch(closeTooltip(String(tooltipId)))
  }

  const toggleClose = useCallback(() => {
    if (disableHoverListener && open) {
      setOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, disableHoverListener])

  useEffect(() => {
    // document.addEventListener('click', toggleClose)
    document.addEventListener('wheel', toggleClose)
    document.addEventListener('scroll', toggleClose)
    document.addEventListener('touchmove', toggleClose)
    // document.addEventListener('touchstart', toggleClose)
    window.addEventListener('resize', toggleClose)
    return () => {
      document.removeEventListener('wheel', toggleClose)
      // document.removeEventListener('click', toggleClose)
      document.removeEventListener('scroll', toggleClose)
      document.removeEventListener('touchmove', toggleClose)
      // document.removeEventListener('touchstart', toggleClose)
      window.removeEventListener('resize', toggleClose)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleClose])

  const dumbRef = () =>
    ({
      clientHeight: 10,
      clientWidth: 10,
      getBoundingClientRect: () => {
        return {
          bottom: mousePos.y + 10,
          height: 10,
          left: mousePos.x,
          right: mousePos.x,
          top: mousePos.y + 10,
          width: 10,
        }
      },
    } as any)

  function toggleOpen(e: React.MouseEvent) {
    if (overCursor) {
      setMousePos({
        x: e.clientX,
        y: e.clientY,
      })
    }

    if (disableHoverListener) {
      setOpen(!open)
      if (disablePropagation) {
        e.stopPropagation()
      }
    }
  }

  const childWithOpen = React.cloneElement(children, {
    [openEvent]: toggleOpen,
  })

  if (overCursor) {
    return (
      <>
        <Popper anchorEl={dumbRef} open={open} placement={placement}>
          <Paper style={{ padding: '5px' }}>
            <div
              style={{
                fontSize: `${11 * size}px`,
                userSelect: 'none',
                whiteSpace: 'pre-line',
                wordBreak: whiteSpace ? 'keep-all' : 'break-word',
              }}
            >
              {title}
            </div>
          </Paper>
        </Popper>
        {childWithOpen}
      </>
    )
  }

  const modifiers = {
    arrow: {
      element: arrowRef,
      enabled: withArrow,
    },
  }

  if (viewportBoundary) {
    modifiers['preventOverflow'] = {
      boundariesElement: 'viewport',
    }
  }

  return (
    <MUToolTip
      ref={tooltipRef}
      disableTouchListener={disableTouchListener}
      open={!disableHoverListener ? undefined : disableHoverListener && open}
      onClose={toggleClose}
      onClick={onClick}
      TransitionProps={{
        timeout: 50,
      }}
      title={withoutWrapper? title:
        <>
          <div
            style={{
              fontSize: `${11 * size}px`,
              userSelect: 'none',
              whiteSpace: 'pre-line',
              wordBreak: whiteSpace ? 'keep-all' : 'break-word',
            }}
          >
            {title}
          </div>
          <div ref={handleRef} className={classes.arrow} />
        </>
      }
      classes={{
        popper: classes.popper,
        tooltip: wordBreakAll ? classes.tooltipBreak : classes.tooltip,
        tooltipPlacementBottom: classes.placementBottom,
        tooltipPlacementLeft: classes.placementLeft,
        tooltipPlacementRight: classes.placementRight,
        tooltipPlacementTop: classes.placementTop,
      }}

      placement={placement}
      PopperProps={{
        popperOptions: {
          modifiers,
        },
      }}
      disableHoverListener={disableHoverListener}
    >
      {disableHoverListener ? (
        <span style={{minWidth: 0}}>{childWithOpen}</span>
      ) : !isRoom ? (
        <span style={{minWidth: 0}}>{childWithOpen}</span>
      ) : (
        childWithOpen
      )}
    </MUToolTip>
  )
}

export default withStyles(styles)(Tooltip)
export type TooltipPlacement =
  | 'bottom'
  | 'left'
  | 'right'
  | 'top'
  | 'bottom-end'
  | 'bottom-start'
  | 'left-end'
  | 'left-start'
  | 'right-end'
  | 'right-start'
  | 'top-end'
  | 'top-start'

export interface ITooltipProps extends Pick<TooltipProps, 'children'> {
  title: string | JSX.Element | React.ReactNode
  openEvent?: string
  isRoom?: boolean
  classes?: {
    arrow: string
    arrowPopper: string
    placementBottom: string
    placementLeft: string
    placementRight: string
    placementTop: string
    popper: string
    tooltip: string
    tooltipBreak: string
  }
  withArrow?: boolean
  size?: number
  onClick?: any
  whiteSpace?: boolean
  placement?: TooltipPlacement
  disablePropagation?: boolean
  disableTouchListener?: boolean
  color?: string
  disableHoverListener?: boolean
  disableClickListener?: boolean
  wordBreakAll?: boolean
  overCursor?: boolean
  tooltipId?: number | string
  viewportBoundary?: boolean
  withoutWrapper?: boolean
}
